import type { InvoiceData } from "@/types";
import { Page, Text, View, Document, StyleSheet, Font, Svg, Line } from '@react-pdf/renderer';
import { currenciesMap } from "../invoiceArea/currencies";
import { formatPrice } from "@/utils";

interface Props {
    data: InvoiceData
}
const PdfDocument = ({ data }: Props) => {
    const { invoiceNumber,
        dateIssued,
        dateDue,
        invoiceName,
        billFrom,
        billFromAddress,
        billFromEmail,
        billFromPhone,
        billFromCountry,
        billTo,
        billToAddress,
        billToCountry,
        billToContact: { email, phone },
        tasks,
        paymentTerms,
        clientNotes,
        invoiceStub,
        totalToPay,
        currency
    } = data;
    const toPay = formatPrice(totalToPay);
    return <Document>
        <Page size={{ width: 8.5 * 72, height: 11 * 72 }} >
            <View style={styles.content}>
                <View style={styles.cardBody}>
                    <View style={styles.invoiceName}>
                        <Text style={styles.title}>Invoice</Text>
                    </View>
                    <View style={styles.billDetails}>
                        <View style={styles.billTo}>
                            <Text style={{ ...styles.sectionLabel, color: "#0a2e47" }}>Bill to:</Text>
                            <Text>{billTo}</Text>
                            {billToAddress && <Text>{billToAddress}</Text>}
                            {billToCountry && <Text>{billToCountry}</Text>}
                            {email && <Text>{email}</Text>}
                            {phone && <Text>{phone}</Text>}
                        </View>
                        <View style={styles.billFrom}>
                            <Text style={{ ...styles.sectionLabel, color: "#0a2e47" }}>Bill from:</Text>
                            <Text>{billFrom}</Text>
                            {billFromAddress && <Text>{billFromAddress}</Text>}
                            {billFromCountry && <Text>{billFromCountry}</Text>}
                            {billFromEmail && <Text>Email: {billFromEmail}</Text>}
                            {billFromPhone && <Text>{billFromPhone}</Text>}
                        </View>

                    </View>
                    <Svg style={{ width: "100%", height: 15, marginTop: "20px" }}>
                        <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#e8e9eb" />
                    </Svg>
                    <View style={styles.total}>
                        <View style={styles.totalSection}>
                            <Text>Invoice #{invoiceNumber}</Text>
                            <Text>{invoiceName}</Text>
                            <Text>Issued: {dateIssued}</Text>
                            <Text>Due: {dateDue}</Text>
                        </View>
                        <View style={{ ...styles.totalSection, textAlign: "right" }}>
                            <Text style={styles.sectionLabel}>Total:</Text>
                            <Text style={{ ...styles.title, fontSize: "0.85rem" }}>
                                {currenciesMap.get(currency)?.symbol}{toPay}
                            </Text>
                        </View>
                    </View>
                    <View style={styles.tasksContainer}>
                        <View style={styles.taskTitleRow}>
                            <Text style={styles.task}>Item</Text>
                            <Text style={styles.cost}>Cost</Text>
                            <Text style={styles.quantity}>QTY</Text>
                            <Text style={styles.price}>Price</Text>
                        </View>
                        <Svg style={{ width: "100%", height: 10 }}>
                            <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#e8e9eb" />
                        </Svg>
                        {tasks.map(({ taskName, taskCost, taskQuantity, taskDescription }, i) =>
                            <View key={i} wrap={false}>
                                <View style={styles.taskRow} >
                                    <View style={styles.taskContent}>
                                        <Text style={{ color: "#0a2e47" }}>{taskName}</Text>
                                        {taskDescription && <Text style={styles.taskDescription}>{taskDescription}</Text>}
                                    </View>
                                    <Text style={styles.costContent}>
                                        {currenciesMap.get(currency)?.symbol}{formatPrice(taskCost)}
                                    </Text>
                                    <Text style={styles.quantityContent}>{taskQuantity}</Text>
                                    <Text style={styles.priceContent}>
                                        {currenciesMap.get(currency)?.symbol}{formatPrice(taskQuantity * taskCost)}
                                    </Text>
                                </View>
                                <Svg style={{ width: "100%", height: 5 }}>
                                    <Line x1="0" y1="2" x2="1000" y2="2" strokeWidth="1" stroke="#e8e9eb" />
                                </Svg>
                            </View>)}
                    </View>
                    <View style={styles.paymentTerms} wrap={false}>
                        <View>
                            {paymentTerms && <View wrap={false}>
                                <Text style={{ ...styles.sectionLabel, color: "#4e606b" }}>Payment Terms</Text>
                                <Text style={{ width: "50%", lineHeight: "14px", fontWeight: 400, marginTop: "5px" }}>{paymentTerms}</Text>
                            </View>}
                        </View>
                        <View style={styles.paymentTermsTotal}>
                            <View style={{ ...styles.paymentTermsAmount }}>
                                <Text>Subtotal:</Text>
                                <Text style={{ color: "#0a2e47" }}>{currenciesMap.get(currency)?.symbol}{toPay}</Text>
                            </View>
                            <Svg style={{ width: "100%", height: 10 }}>
                                <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#e8e9eb" />
                            </Svg>
                            <View style={{ ...styles.paymentTermsAmount }}>
                                <Text>Total:</Text>
                                <Text style={{ color: "#0a2e47" }}>{currenciesMap.get(currency)?.symbol}{toPay}</Text>
                            </View>
                            <Svg style={{ width: "100%", height: 10 }}>
                                <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#e8e9eb" />
                            </Svg>
                            <View style={{ ...styles.paymentTermsAmount }}>
                                <Text>Paid to date:</Text>
                                <Text style={{ color: "#0a2e47" }}>{currenciesMap.get(currency)?.symbol}{formatPrice(0)}</Text>
                            </View>
                            <Svg style={{ width: "100%", height: 10 }}>
                                <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#e8e9eb" />
                            </Svg>
                            <View style={styles.amountDue}>
                                <Text>Amount due ({currenciesMap.get(currency)?.code}): </Text>
                                <Text>{currenciesMap.get(currency)?.symbol}{toPay}</Text>
                            </View>
                        </View>
                    </View>
                    <View style={{ ...styles.paymentTerms, margin: 0 }} wrap={false}>
                        <View style={styles.paymentTermsNotes}>
                            {clientNotes && <View style={{ marginTop: "10px" }}>
                                <Text style={{ ...styles.sectionLabel, color: "#4e606b" }}>Client Notes</Text>
                                <Text style={{ lineHeight: "14px", fontWeight: 400, marginTop: "5px" }}>{clientNotes}</Text>
                            </View>}
                        </View>
                    </View>
                    {invoiceStub && <View style={{ marginTop: "20px" }} wrap={false}>
                        <Svg style={{ width: "100%", height: 10 }}>
                            <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#898989" strokeDasharray="2 2" />
                        </Svg>
                        <View style={{ marginTop: "10px", marginBottom: "10px" }}>
                            <Text>Payment stub</Text>
                            <View style={styles.invoiceStub}>
                                <View style={{ ...styles.billFrom, textAlign: "left" }}>
                                    <Text style={{ ...styles.sectionLabel, color: "#0a2e47" }}>Bill from:</Text>
                                    <Text>{billFrom}</Text>
                                    {billFromAddress && <Text>{billFromAddress}</Text>}
                                    {billFromCountry && <Text>{billFromCountry}</Text>}
                                    {billFromEmail && <Text>{billFromEmail}</Text>}
                                    {billFromPhone && <Text>{billFromPhone}</Text>}
                                </View>
                                <View style={styles.stubAmount}>
                                    <View style={styles.paymentTermsAmount}>
                                        <Text>Client</Text>
                                        <Text style={{ color: "#0a2e47" }}>{billTo}</Text>
                                    </View>
                                    <View style={styles.paymentTermsAmount}>
                                        <Text>Invoice #</Text>
                                        <Text style={{ color: "#0a2e47" }}>{invoiceNumber}</Text>
                                    </View>
                                    <Svg style={{ width: "100%", height: 10 }}>
                                        <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#e8e9eb" />
                                    </Svg>
                                    <View style={styles.paymentTermsAmount}>
                                        <Text>Paid to date:</Text>
                                        <Text style={{ color: "#0a2e47" }}>{currenciesMap.get(currency)?.symbol}{formatPrice(0)}</Text>
                                    </View>
                                    <View style={styles.paymentTermsAmount}>
                                        <Text>Amount Due ({currenciesMap.get(currency)?.code}):</Text>
                                        <Text style={{ color: "#0a2e47" }} >{currenciesMap.get(currency)?.symbol}{toPay}</Text>
                                    </View>
                                </View>
                            </View>
                        </View>

                        <Svg style={{ width: "100%", height: 10 }}>
                            <Line x1="0" y1="5" x2="1000" y2="5" strokeWidth="1" stroke="#898989" strokeDasharray="2 2" />
                        </Svg>
                    </View>}
                </View>
            </View>
        </Page>
    </Document >
}

Font.register({
    family: 'NimbusSans',
    fonts: [
        {
            src: "/fonts/NimbusSansRegular.ttf",
            fontWeight: 400,
        },
        {
            src: "/fonts/NimbusSansBold.ttf",
            fontWeight: 700,
        }
    ],
});

const styles = StyleSheet.create({
    total: { display: "flex", flexDirection: "row", justifyContent: "space-between", marginTop: "10px" },
    totalSection: { display: "flex", flexDirection: "column", width: "50%", gap: "5px" },
    divider: { marginVertical: 10 },
    title: { fontSize: "1.2rem", fontWeight: 700, color: "#4358b5" },
    label: { fontSize: "6px" },
    content: { padding: "20px", fontFamily: "NimbusSans" },
    cardBody: { paddingLeft: "10px", paddingRight: "10px", fontSize: "0.41rem", color: "#4e606b" },
    invoiceName: { display: "flex", flexDirection: "column", gap: "10px" },
    sectionLabel: { textTransform: "uppercase", fontWeight: 700, fontSize: "6px" },
    billDetails: { display: "flex", flexDirection: "row", marginTop: "20px", justifyContent: "space-between" },
    billFrom: { display: "flex", flexDirection: "column", width: "50%", gap: "3px", textAlign: "right" },
    billTo: { display: "flex", flexDirection: "column", width: "50%", gap: "3px" },
    tasksContainer: { display: "flex", flexDirection: "column", marginTop: "20px" },
    taskTitleRow: { display: "flex", flexDirection: "row", justifyContent: "space-between", textAlign: "left", borderRadius: "8px" },
    taskRow: { display: "flex", flexDirection: "row", justifyContent: "space-between", textAlign: "left", },
    taskDescription: { lineHeight: "14px", fontWeight: 400, marginTop: "5px", color: "#4e606b" },
    task: { width: "50%", display: "flex", flexDirection: "column", gap: "5px", textTransform: "uppercase", fontWeight: 700, fontSize: "6px" },
    cost: { width: "20%", textTransform: "uppercase", textAlign: "left", fontWeight: 700, fontSize: "6px" },
    quantity: { width: "10%", textTransform: "uppercase", fontWeight: 700, fontSize: "6px" },
    price: { width: "20%", textAlign: "right", textTransform: "uppercase", fontWeight: 700, color: "#0a2e47", fontSize: "6px" },
    taskContent: { width: "50%", color: "#0a2e47" },
    costContent: { width: "20%", color: "#0a2e47" },
    quantityContent: { width: "10%" },
    termsText: { fontWeight: 700 },
    amountDue: { color: "#4358b5", display: "flex", flexDirection: "row", justifyContent: "space-between", fontWeight: 700 },
    priceContent: { width: "20%", textAlign: "right", color: "#0a2e47" },
    paymentTerms: { display: "flex", flexDirection: "row", justifyContent: "space-between" },
    paymentTermsNotes: { width: "45%", display: "flex", flexDirection: "column", gap: "10px" },
    paymentTermsTotal: { width: "40%", display: "flex", flexDirection: "column", gap: "2px" },
    paymentTermsAmount: { display: "flex", flexDirection: "row", justifyContent: "space-between" },
    invoiceStub: { display: "flex", marginTop: "10px", flexDirection: "row", justifyContent: "space-between" },
    stubDetails: { width: "45%", display: "flex", flexDirection: "column", gap: "10px", borderRadius: "8px" },
    stubAmount: { width: "45%", display: "flex", flexDirection: "column", gap: "5px", },
});


export default PdfDocument;